/**
 * The trees an admin keeps, and which one is running (ACL-340).
 *
 * Its own module beside `queries-intake.ts`: a tree is edited on a different screen and at a
 * different moment from a draft's graph, and both files stay small this way.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useKeys } from "@/lib/queries";

export interface TreeSummary {
  id: number;
  slug: string;
  title: string;
  note: string | null;
  position: number;
  /** The one tree the tenant is running. Exactly one is active at a time. */
  active: boolean;
  archived: boolean;
  draft_id: number | null;
  published_id: number | null;
  published_version: number | null;
}

export interface TreeList {
  trees: TreeSummary[];
  /** What is wrong with the order: a tree that takes every lead above one that would never run. */
  warnings: string[];
}

function treeKeys(prefix: readonly unknown[]) {
  return {
    trees: () => [...prefix, "intake-trees"] as const,
    flows: () => [...prefix, "intake-flows"] as const,
  };
}

function useTreeKeys() {
  const keys = useKeys();
  return treeKeys(keys.scoped(["intake"]).slice(0, -1));
}

/** Every tree, in the order the engine tries them. Admin only. */
export function useTrees(includeArchived = false) {
  const keys = useTreeKeys();
  return useQuery({
    queryKey: [...keys.trees(), { includeArchived }],
    queryFn: () =>
      api.get<TreeList>(`/intake-trees${includeArchived ? "?include_archived=true" : ""}`),
  });
}

export function useCreateTree() {
  const keys = useTreeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { slug: string; title: string; note?: string }) =>
      api.post<TreeSummary>("/intake-trees", {
        slug: body.slug,
        title: body.title,
        note: body.note ?? null,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.trees() }),
  });
}

export function useUpdateTree() {
  const keys = useTreeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      treeId,
      ...body
    }: {
      treeId: number;
      title?: string;
      note?: string | null;
    }) => api.patch<TreeSummary>(`/intake-trees/${treeId}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.trees() }),
  });
}

/** Copy a tree and the graph it is working on, as a new draft. The copy is never the one running. */
export function useDuplicateTree() {
  const keys = useTreeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ treeId, slug, title }: { treeId: number; slug: string; title: string }) =>
      api.post<TreeSummary>(`/intake-trees/${treeId}/duplicate`, { slug, title }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.trees() });
      void qc.invalidateQueries({ queryKey: keys.flows() });
    },
  });
}

export function useReorderTrees() {
  const keys = useTreeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: number[]) => api.post<TreeList>("/intake-trees/order", { order }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.trees() }),
  });
}

/** Make one tree the tenant's running one, which stands the previous one down. */
export function useActivateTree() {
  const keys = useTreeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ treeId, active }: { treeId: number; active: boolean }) =>
      api.post<TreeList>(`/intake-trees/${treeId}/${active ? "activate" : "deactivate"}`, {}),
    // The whole list comes back, because activating one tree changes another.
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.trees() }),
  });
}

export function useArchiveTree() {
  const keys = useTreeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ treeId, archived }: { treeId: number; archived: boolean }) =>
      api.post<TreeSummary>(`/intake-trees/${treeId}/${archived ? "archive" : "restore"}`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.trees() }),
  });
}
